import { useState, type CSSProperties, type FC } from 'react';
import type { EditorJointArticle } from '../types';
import { CloseIcon, PlusIcon } from './EditorIcons';

interface EditorJointArticlesOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  articles: EditorJointArticle[];
  onCreateArticle: (payload: Partial<EditorJointArticle>) => Promise<EditorJointArticle>;
}

function statusAccent(st: string): string {
  switch (st) {
    case 'Опубликовано':
    case 'Опубликовано / 2026':
      return '#49ddb7';
    case 'На рассмотрении':
      return '#e2ad3a';
    case 'Рецензирование':
      return '#41d2ff';
    case 'Редакционный процесс':
      return '#9e80ff';
    case 'Рабочая база':
      return '#66b7e8';
    default:
      return '#8faebb';
  }
}

/** Joint articles — cab-overlay chrome + cabinet-* body. */
export const EditorJointArticlesOverlay: FC<EditorJointArticlesOverlayProps> = ({
  isOpen,
  onClose,
  articles,
  onCreateArticle,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [journal, setJournal] = useState('');
  const [coauthors, setCoauthors] = useState('Владелец + Алтын Бакытжанова');
  const [status, setStatus] = useState('На рассмотрении');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<EditorJointArticle | null>(null);

  if (!isOpen) return null;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !journal.trim()) return;
    try {
      setIsSubmitting(true);
      await onCreateArticle({
        title,
        journal,
        coauthors,
        status,
        year_or_date: '2026',
        stage: 'В процессе',
        payment_status: 'Уточняется',
        source_catalog: 'KAZAKHSTAN_CONCEPT_2021_2025',
      });
      setTitle('');
      setJournal('');
      setShowForm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Публикации</small>
              <h2>Совместные статьи</h2>
              <p>Реестр совместных публикаций и заказов</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button
              type="button"
              className="cab-btn primary"
              onClick={() => setShowForm(!showForm)}
            >
              <PlusIcon className="w-4 h-4" />
              Новый заказ
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="cab-body">
          <section className="cabinet-hero" style={{ '--accent': '#ff7a64' } as CSSProperties}>
            <div>
              <div className="cabinet-eyebrow">Совместные статьи / заказы</div>
              <h2>Совместные статьи</h2>
              <p>
                Общая рабочая карточка владельца и научного партнёра. Изменения сохраняются в общей
                записи.
              </p>
            </div>
          </section>

          <div className="cabinet-note" style={{ marginTop: 14 }}>
            <strong>Связанный реестр: «Заказы клиентов»</strong>
            <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12, lineHeight: 1.5 }}>
              Доступен каталог концепций Казахстана 2021–2025. Сведения о заказе находятся в связанной карточке.
            </p>
          </div>

          <section className="cabinet-kpis" style={{ marginTop: 14 }}>
            <article className="cabinet-kpi" style={{ '--accent': '#8faebb' } as CSSProperties}>
              <small>Все карточки</small>
              <strong>{articles.length || 5}</strong>
            </article>
            <article className="cabinet-kpi" style={{ '--accent': '#41d2ff' } as CSSProperties}>
              <small>Этапы</small>
              <strong>5</strong>
            </article>
            <article className="cabinet-kpi" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
              <small>В процессе</small>
              <strong>3</strong>
            </article>
            <article className="cabinet-kpi" style={{ '--accent': '#9e80ff' } as CSSProperties}>
              <small>Совместных редактора</small>
              <strong>2</strong>
            </article>
          </section>

          {showForm ? (
            <form
              onSubmit={handleCreate}
              className="cabinet-card"
              style={{ '--accent': '#41d2ff', marginTop: 14 } as CSSProperties}
            >
              <h4 style={{ margin: '0 0 12px', fontSize: 13 }}>
                Добавление совместной статьи / публикации
              </h4>
              <div className="cabinet-grid2">
                <label className="cabinet-field">
                  <span>Название статьи</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Когнитивная лингвистика в казахском дискурсе"
                  />
                </label>
                <label className="cabinet-field">
                  <span>Журнал</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    required
                    value={journal}
                    onChange={(e) => setJournal(e.target.value)}
                    placeholder="World Journal of English Language"
                  />
                </label>
                <label className="cabinet-field">
                  <span>Соавторы</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    value={coauthors}
                    onChange={(e) => setCoauthors(e.target.value)}
                  />
                </label>
                <label className="cabinet-field">
                  <span>Статус</span>
                  <select
                    className="cabinet-select"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="На рассмотрении">На рассмотрении</option>
                    <option value="Рецензирование">Рецензирование</option>
                    <option value="Редакционный процесс">Редакционный процесс</option>
                    <option value="Опубликовано">Опубликовано</option>
                    <option value="Рабочая база">Рабочая база</option>
                  </select>
                </label>
              </div>
              <div className="cabinet-actions" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn" onClick={() => setShowForm(false)}>
                  Отмена
                </button>
                <button type="submit" className="cabinet-btn primary" disabled={isSubmitting}>
                  Создать заказ
                </button>
              </div>
            </form>
          ) : null}

          <div className="cabinet-stack" style={{ marginTop: 14 }}>
            {articles.map((art) => (
              <article
                key={art.id}
                className="cabinet-card"
                style={{ '--accent': statusAccent(art.status) } as CSSProperties}
              >
                <div className="cabinet-actions">
                  <strong style={{ flex: 1 }}>{art.title}</strong>
                  <span
                    className="cabinet-badge"
                    style={{ '--badge': statusAccent(art.status) } as CSSProperties}
                  >
                    {art.status === 'Peer review' ? 'Рецензирование' : art.status}
                  </span>
                </div>
                <p style={{ margin: '8px 0 0', color: '#8faebb', fontSize: 12 }}>
                  {art.journal} · {art.year_or_date}
                </p>
                <div className="cabinet-actions" style={{ marginTop: 10 }}>
                  <div>
                    <span style={{ display: 'block', fontSize: 12 }}>{art.coauthors}</span>
                    <span style={{ fontSize: 11, color: '#8faebb' }}>
                      Оплата: {art.payment_status}
                    </span>
                  </div>
                  <span className="cabinet-spacer" />
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => setSelectedArticle(art)}
                  >
                    Открыть
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      {selectedArticle ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal" style={{ maxWidth: 560 }}>
            <header className="cabinet-modal-head">
              <div>
                <h2>
                  {selectedArticle.article_code} · Карточка совместной статьи
                </h2>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setSelectedArticle(null)}
                aria-label="Закрыть"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </header>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <strong>{selectedArticle.title}</strong>
                <p>Журнал: {selectedArticle.journal}</p>
                <p>Соавторы: {selectedArticle.coauthors}</p>
                <p>Статус: {selectedArticle.status}</p>
                <p>Этап: {selectedArticle.stage}</p>
                <p>Оплата: {selectedArticle.payment_status}</p>
                <p style={{ color: '#8faebb', fontSize: 11 }}>
                  Источник: {selectedArticle.source_catalog || 'KAZAKHSTAN_CONCEPT_2021_2025'}
                </p>
              </div>
              <div className="cabinet-actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="cabinet-btn"
                  onClick={() => setSelectedArticle(null)}
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
